import { useEffect, useRef } from "react";
import { API_URL, getToken } from "@/lib/api";

const WS_BASE = API_URL.replace(/^http/, "ws");
const PING_MS = 25000;
const MAX_BACKOFF_MS = 15000;

/**
 * Subscribes to a device's live feed over WebSocket. Calls onMessage(msg) for
 * `{type:"live", live, last_seen}` readings and `{type:"device", device}`
 * record changes (client name, location, status...). Pass no deviceId to
 * watch every device in the tenant (`device` / `device_removed` events).
 * Reconnects with backoff, and pings so idle proxies don't drop the connection.
 */
export function useDeviceSocket(deviceId, onMessage, { all = false, admin = false } = {}) {
  const handler = useRef(onMessage);
  handler.current = onMessage;

  useEffect(() => {
    if (!deviceId && !all) return undefined;
    let ws, ping, retry, attempt = 0, closed = false;
    const path = admin ? "/ws/admin/devices" : all ? "/ws/devices" : `/ws/devices/${deviceId}`;

    const connect = () => {
      ws = new WebSocket(`${WS_BASE}${path}`);
      ws.onopen = () => {
        attempt = 0;
        ws.send(JSON.stringify({ token: getToken() }));
        ping = setInterval(() => ws.readyState === WebSocket.OPEN && ws.send("ping"), PING_MS);
      };
      ws.onmessage = (e) => {
        if (e.data === "pong") return;
        try {
          const msg = JSON.parse(e.data);
          handler.current(msg);
        } catch { /* ignore malformed frame */ }
      };
      ws.onclose = (e) => {
        clearInterval(ping);
        // 4401 = rejected (bad/expired token or not this tenant's device): retrying can't help.
        if (closed || e.code === 4401) return;
        retry = setTimeout(connect, Math.min(1000 * 2 ** attempt++, MAX_BACKOFF_MS));
      };
    };

    connect();
    return () => { closed = true; clearInterval(ping); clearTimeout(retry); ws && ws.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, all, admin]);
}

/** Live readings for one device: onLive({ live, last_seen }); onDevice(device) on record changes. */
export default function useDeviceLive(deviceId, onLive, onDevice, onInvalidate) {
  const dev = useRef(onDevice);
  dev.current = onDevice;
  const inv = useRef(onInvalidate);
  inv.current = onInvalidate;
  useDeviceSocket(deviceId, (msg) => {
    if (msg.type === "live") onLive(msg);
    else if (msg.type === "device" && dev.current) dev.current(msg.device);
    else if (msg.type === "invalidate" && inv.current) inv.current(msg.topics);
  });
}

function applyListEvent(setDevices, msg, onUnknown) {
  if (msg.type === "device") {
    setDevices((list) => {
      const i = list.findIndex((d) => d.id === msg.device.id);
      if (i < 0) { onUnknown?.(); return onUnknown ? list : [msg.device, ...list]; }
      const next = list.slice();
      next[i] = { ...list[i], ...msg.device };  // keep fields only the admin list adds (tenant_name...)
      return next;
    });
  } else if (msg.type === "device_live") {
    setDevices((list) => list.map((d) => (d.id === msg.id
      ? { ...d, live: { ...d.live, ...msg.live }, last_seen: msg.last_seen, status: "active" } : d)));
  } else if (msg.type === "device_removed") {
    setDevices((list) => list.filter((d) => d.id !== msg.id));
  }
}

/** Keeps a tenant's device list in sync (client details, status, live wattage, add/remove). */
export function useDeviceListLive(setDevices) {
  useDeviceSocket(null, (msg) => applyListEvent(setDevices, msg), { all: true });
}

/** Super-admin version across all tenants; onUnknown() (e.g. a reload) runs when an unseen device appears. */
export function useAdminDeviceListLive(setDevices, onUnknown) {
  useDeviceSocket(null, (msg) => applyListEvent(setDevices, msg, onUnknown), { all: true, admin: true });
}

/**
 * Calls reload() when the server says any of `topics` changed ("reports", "history", "orders",
 * "catalog", "settings", "regions", "devices", "tenants"). Bursts are coalesced into one reload.
 * `admin` listens on the super-admin feed (all tenants) instead of the tenant's own.
 */
export function useInvalidate(topics, reload, { admin = false } = {}) {
  const fn = useRef(reload);
  fn.current = reload;
  const want = useRef(topics);
  want.current = topics;
  const timer = useRef();
  useEffect(() => () => clearTimeout(timer.current), []);
  useDeviceSocket(null, (msg) => {
    const got = msg.type === "invalidate" ? msg.topics
      : msg.type === "device" || msg.type === "device_removed" ? ["devices"] : [];
    if (!got.some((t) => want.current.includes(t))) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => fn.current(), 400);
  }, { all: true, admin });
}
